import { Zap, Award } from 'lucide-react';
import { getXPForLevel, getLevelProgress } from '../../lib/gamification/xpEngine';

interface XPBarProps {
  currentXP: number;
  currentLevel: number;
  showDetailed?: boolean;
  className?: string;
}

export function XPBar({ currentXP, currentLevel, showDetailed = false, className = '' }: XPBarProps) {
  const currentLevelXP = getXPForLevel(currentLevel);
  const nextLevelXP = getXPForLevel(currentLevel + 1);
  const progress = getLevelProgress(currentXP, currentLevel);
  const xpInLevel = currentXP - currentLevelXP;
  const xpNeeded = nextLevelXP - currentLevelXP;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Level and XP Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          <span className="font-bold text-gray-900">Level {currentLevel}</span>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <Zap className="w-4 h-4 text-yellow-500" />
          <span className="font-semibold">{currentXP.toLocaleString()} XP</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        >
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
        </div>
      </div>

      {/* Detailed Progress Info */}
      {showDetailed && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{xpInLevel.toLocaleString()} / {xpNeeded.toLocaleString()} XP</span>
          <span>{xpNeeded - xpInLevel} XP to Level {currentLevel + 1}</span>
        </div>
      )}
    </div>
  );
}
